const IMAGE_UPLOAD_TYPES = [
  { extensions: ['.jpg', '.jpeg'], mimeTypes: ['image/jpeg', 'image/jpg'] },
  { extensions: ['.png'], mimeTypes: ['image/png'] },
  { extensions: ['.webp'], mimeTypes: ['image/webp'] },
  { extensions: ['.avif'], mimeTypes: ['image/avif'] },
  { extensions: ['.heic'], mimeTypes: ['image/heic'] },
  { extensions: ['.heif'], mimeTypes: ['image/heif'] },
];
const TYPE_BY_MIME = new Map(
  IMAGE_UPLOAD_TYPES.flatMap((spec) => spec.mimeTypes.map((mimeType) => [mimeType, spec]))
);
const TYPE_BY_EXTENSION = new Map(
  IMAGE_UPLOAD_TYPES.flatMap((spec) => spec.extensions.map((extension) => [extension, spec]))
);
const HEIF_MIME_TYPES = new Set(['image/heic', 'image/heif']);
const HEIF_EXTENSIONS = new Set(['.heic', '.heif']);
const JPEG_MIME_TYPES = new Set(['image/jpeg', 'image/jpg']);
type ImageLike = { name?: string; type?: string; lastModified?: number };

export const IMAGE_UPLOAD_ACCEPT_ATTR = IMAGE_UPLOAD_TYPES
  .flatMap(({ extensions, mimeTypes }) => [...extensions, ...mimeTypes])
  .join(',');

const extensionOf = (name: unknown) => {
  const normalized = String(name || '').trim().toLowerCase();
  const index = normalized.lastIndexOf('.');
  return index >= 0 ? normalized.slice(index) : '';
};

export const isImageUploadFile = (file: ImageLike | null | undefined) => {
  const mimeType = String(file?.type || '').trim().toLowerCase();
  const extension = extensionOf(file?.name);
  const mimeSpec = TYPE_BY_MIME.get(mimeType);
  const extensionSpec = TYPE_BY_EXTENSION.get(extension);
  const mimeIsUnknown = !mimeType || mimeType === 'application/octet-stream';
  if (HEIF_EXTENSIONS.has(extension) && JPEG_MIME_TYPES.has(mimeType)) return true;
  if (mimeSpec && extensionSpec && mimeSpec !== extensionSpec) return false;
  if (extensionSpec && (mimeSpec || mimeIsUnknown)) return true;
  return !extension && Boolean(mimeSpec);
};

export const isHeifImageUploadFile = (file: ImageLike | null | undefined) => {
  const mimeType = String(file?.type || '').trim().toLowerCase();
  return HEIF_MIME_TYPES.has(mimeType) || HEIF_EXTENSIONS.has(extensionOf(file?.name));
};

const compatibleJpegName = (name: unknown) => {
  const normalized = String(name || '').trim() || `image-${Date.now()}.heic`;
  return /\.(?:heic|heif)$/i.test(normalized)
    ? normalized.replace(/\.(?:heic|heif)$/i, '.jpg')
    : `${normalized}.jpg`;
};

const loadBrowserImage = (sourceUrl: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('HEIC / HEIF画像をこのブラウザで読み込めませんでした。'));
  image.src = sourceUrl;
});

const canvasToJpeg = (canvas: HTMLCanvasElement) => new Promise<Blob>((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error('HEIC / HEIF画像をJPEGへ変換できませんでした。'));
  }, 'image/jpeg', 0.92);
});

export async function normalizeImageForUpload(file: File): Promise<File> {
  if (!file || !isHeifImageUploadFile(file)) return file;
  const mimeType = String(file.type || '').trim().toLowerCase();
  if (HEIF_EXTENSIONS.has(extensionOf(file.name)) && JPEG_MIME_TYPES.has(mimeType)) {
    return new File([file], compatibleJpegName(file.name), {
      type: 'image/jpeg',
      lastModified: Number(file.lastModified || Date.now()),
    });
  }
  if (
    typeof Image === 'undefined'
    || typeof document === 'undefined'
    || typeof URL === 'undefined'
    || typeof URL.createObjectURL !== 'function'
  ) throw new Error('HEIC / HEIF画像はCasticartアプリ、または対応ブラウザから送信してください。');

  const sourceUrl = URL.createObjectURL(file);
  try {
    const image = await loadBrowserImage(sourceUrl);
    const sourceWidth = Number(image.naturalWidth || image.width || 0);
    const sourceHeight = Number(image.naturalHeight || image.height || 0);
    if (!sourceWidth || !sourceHeight) throw new Error('画像サイズを確認できませんでした。');
    const scale = Math.min(1, 4000 / Math.max(sourceWidth, sourceHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sourceWidth * scale));
    canvas.height = Math.max(1, Math.round(sourceHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('画像変換を開始できませんでした。');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const jpeg = await canvasToJpeg(canvas);
    return new File([jpeg], compatibleJpegName(file.name), {
      type: 'image/jpeg',
      lastModified: Number(file.lastModified || Date.now()),
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : '';
    throw new Error(`HEIC / HEIF画像を送信できる形式へ変換できませんでした${message ? `（${message}）` : ''}`);
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

export async function normalizeImagesForUpload(files: FileList | readonly File[]): Promise<File[]> {
  return Promise.all(Array.from(files || []).filter(Boolean).map(normalizeImageForUpload));
}

export async function normalizeOptionalImageForUpload(
  file: File | null | undefined,
  reportError: (message: string) => void
): Promise<File | null | undefined> {
  try {
    return file ? await normalizeImageForUpload(file) : null;
  } catch (error: unknown) {
    reportError(error instanceof Error && error.message
      ? error.message
      : '画像を送信できる形式へ変換できませんでした');
    return undefined;
  }
}
